import {
  AlertTriangle, Banknote, CalendarClock, CheckCircle2, Clock, CircleAlert, Loader2, Sparkles, Truck,
} from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useOpenOnce } from '../../lib/in-app-history'
import type { Lang } from '../../lib/i18n'
import {
  getForwardAdvanceTargets, type BoardOrderRow, type FinalCheckFact, type OrderStatusValue, formatOrderId,
} from '../../lib/orders-api'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardFooter, CardHeader } from '../../components/ui/card'
import { Input } from '../../components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover'
import { cn } from '../../components/ui/utils'
import { Ltr } from '../orders/Ltr'
import { formatMoney } from '../orders/format'
import { ConfirmDialog } from '../orders/ConfirmDialog'
import { FINAL_CHECK_ACTION_LABEL, STATUS_ADVANCE_LABEL, STATUS_BADGE_LABEL } from '../orders/orders-i18n'
import { computeAging, formatAgingDuration, type AgingBand, type AgingResult } from './aging'
import { bt, fillSlots } from './board-i18n'

// R197 (b): per-row state of the one-click final check, kept by the Board.
export type FinalCheckRowState =
  | { kind: 'idle' }
  | { kind: 'pending' }
  | { kind: 'queued' }
  | { kind: 'rejected'; message: string }

// The one-click button is HOP's own final-check gate (HeaderSection): hop or admin. A UI gate,
// not a wall -- the server admits more (owner, architect), who keep the two-click path.
function canFinalCheck(roles: string[]): boolean {
  return roles.includes('hop') || roles.includes('admin')
}

const DECIMAL = /^-?\d+(\.\d+)?$/

// 'changed' and 'unchanged' only when the flag is a boolean AND both totals are finite decimal
// strings AND a currency is present. Anything else is 'unreadable', and unreadable means ask.
function readFact(fact: FinalCheckFact): 'changed' | 'unchanged' | 'unreadable' {
  const readable =
    typeof fact.moneyChanged === 'boolean' &&
    typeof fact.totalNow === 'string' && DECIMAL.test(fact.totalNow) && Number.isFinite(Number(fact.totalNow)) &&
    typeof fact.totalAtPlacement === 'string' && DECIMAL.test(fact.totalAtPlacement) &&
    Number.isFinite(Number(fact.totalAtPlacement)) &&
    typeof fact.currency === 'string' && fact.currency.trim() !== ''
  if (!readable) return 'unreadable'
  return fact.moneyChanged ? 'changed' : 'unchanged'
}

function FinalCheckButton({ orderId, fact, state, lang, onFinalCheck, sending }: {
  orderId: string
  fact: FinalCheckFact
  state: FinalCheckRowState
  lang: Lang
  onFinalCheck: (orderId: string, totalNow: string) => void
  sending: boolean
}) {
  if (state.kind === 'pending') {
    return <Button size="sm" className="min-h-[44px]" disabled>{bt(lang, 'finalCheckSending')}</Button>
  }
  if (state.kind === 'queued') {
    return <Button size="sm" className="min-h-[44px]" disabled>{bt(lang, 'finalCheckQueued')}</Button>
  }
  const kind = readFact(fact)
  if (kind === 'unchanged') {
    return (
      <Button size="sm" className="min-h-[44px]" disabled={sending} onClick={() => onFinalCheck(orderId, fact.totalNow)}>
        {FINAL_CHECK_ACTION_LABEL[lang]}
      </Button>
    )
  }
  const button = (
    <Button size="sm" className="min-h-[44px]" disabled={sending}>
      {FINAL_CHECK_ACTION_LABEL[lang]}
    </Button>
  )
  const description = kind === 'changed'
    ? fillSlots(bt(lang, 'finalCheckChanged'), {
        from: formatMoney(fact.totalAtPlacement, fact.currency),
        to: formatMoney(fact.totalNow, fact.currency),
      })
    : bt(lang, 'finalCheckUnreadable')
  return (
    <ConfirmDialog
      trigger={button}
      title={bt(lang, 'finalCheckConfirmTitle')}
      description={description}
      actionLabel={bt(lang, 'finalCheckApprove')}
      dismissLabel={bt(lang, 'finalCheckCancel')}
      onConfirm={() => onFinalCheck(orderId, fact.totalNow)}
    />
  )
}

// D2 (#9088 gavel A): Reject renders ONLY for admin - hop gets prep-advance buttons only.
// The server's A3 guard (orders.service.ts) wants the literal `admin`, so an owner without it
// got a silent 403; every prod owner also holds admin (Rook #54794), so none loses the button.
function canReject(roles: string[]): boolean {
  return roles.includes('admin')
}

const BADGE_VARIANT: Record<OrderStatusValue, 'default' | 'secondary' | 'sage' | 'destructive' | 'outline'> = {
  PENDING: 'outline',
  CONFIRMED: 'default',
  IN_KITCHEN: 'secondary',
  AWAITING_RETAIL_PACK: 'secondary',
  AWAITING_FINAL_CHECK: 'secondary',
  READY_FOR_DISPATCH: 'secondary',
  OUT_FOR_DELIVERY: 'default',
  DELIVERED: 'sage',
  CANCELLED: 'destructive',
  REFUNDED: 'destructive',
}

// order-TYPE border channel. Interpreted as fulfillmentMethod (delivery/pickup) — the
// operationally relevant "type" split for a kitchen/prep triage board — rather than the
// Order.orderType enum (SALE/COMP/LEGACY, a finance/costing classification invisible to
// day-to-day prep). The design-contract doc naming the "order-TYPE" channel wasn't
// present in this worktree to confirm literally; flagged for Rook/DC in RESULTS.md.
const FULFILLMENT_BORDER_CLASS: Record<'delivery' | 'pickup' | 'unknown', string> = {
  delivery: 'border-s-primary',
  pickup: 'border-s-purple',
  unknown: 'border-s-border',
}

// D5 AGING header channel — must not visually collide with the border channel above (a
// full-width header tint vs. a thin start-edge border are distinct positions/shapes, not
// just distinct hues, so the two channels stay legible together per the design contract's
// "never color-alone" spirit).
const AGING_ICON: Record<AgingBand, typeof CheckCircle2> = {
  green: CheckCircle2, yellow: Clock, red: AlertTriangle, neutral: CalendarClock,
}
const AGING_HEADER_CLASS: Record<AgingBand, string> = {
  green: 'bg-sage/15', yellow: 'bg-amber-50', red: 'bg-destructive/10', neutral: 'bg-muted/50',
}
const AGING_TEXT_CLASS: Record<AgingBand, string> = {
  green: 'text-sage-ink', yellow: 'text-amber-700', red: 'text-destructive', neutral: 'text-muted-foreground',
}

function AgingChip({ aging, lang }: { aging: AgingResult; lang: Lang }) {
  const Icon = AGING_ICON[aging.band]
  let label: string
  let value: string | null = null
  if (aging.kind === 'neutral') {
    label = bt(lang, 'agingScheduled')
  } else if (aging.kind === 'handoff') {
    // §4-RULED ①: reuses the existing bilingual status label — no new copy, see aging.ts.
    // bench-BOARD-LATE-SAYS-LATE: a delivery date already past (minutes < 0) swaps the
    // status label for the existing agingOverdue label; band/value stay as ruled above.
    label = (aging.minutes ?? 0) < 0 ? bt(lang, 'agingOverdue') : STATUS_BADGE_LABEL.READY_FOR_DISPATCH[lang]
    value = formatAgingDuration(Math.abs(aging.minutes ?? 0))
  } else if (aging.kind === 'elapsed') {
    label = bt(lang, 'agingWaiting')
    value = formatAgingDuration(aging.minutes ?? 0)
  } else if ((aging.minutes ?? 0) < 0) {
    label = bt(lang, 'agingOverdue')
    value = formatAgingDuration(Math.abs(aging.minutes ?? 0))
  } else {
    label = bt(lang, 'agingDue')
    value = formatAgingDuration(aging.minutes ?? 0)
  }
  return (
    <span className={cn('inline-flex shrink-0 items-center gap-1 text-xs font-medium', AGING_TEXT_CLASS[aging.band])}>
      <Icon className="h-3.5 w-3.5" aria-hidden />
      <span>{label}</span>
      {value !== null && <Ltr>{value}</Ltr>}
    </span>
  )
}

function DispatchBadge({ dispatch, lang }: { dispatch: NonNullable<BoardOrderRow['dispatch']>; lang: Lang }) {
  if (dispatch.exceptionReason) {
    return (
      <p className="flex items-center gap-1.5 rounded-md bg-destructive/10 px-2 py-1 text-xs font-medium text-destructive">
        <CircleAlert className="h-3.5 w-3.5" aria-hidden />
        <span>{bt(lang, 'dispatchException')}</span>
      </p>
    )
  }
  return (
    <p className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md bg-muted/50 px-2 py-1 text-xs text-muted-foreground">
      {dispatch.driverId && (
        <span className="inline-flex items-center gap-1.5">
          <Truck className="h-3.5 w-3.5" aria-hidden />
          {bt(lang, 'dispatchDriverAssigned')}
        </span>
      )}
      <span className={cn('inline-flex items-center gap-1.5', dispatch.cashCollected ? 'text-sage-ink' : 'text-amber-700')}>
        <Banknote className="h-3.5 w-3.5" aria-hidden />
        {dispatch.cashCollected ? bt(lang, 'dispatchCashCollected') : bt(lang, 'dispatchCashToCollect')}
      </span>
    </p>
  )
}

function RejectPopover({ lang, onReject, sending }: { lang: Lang; onReject: (notes: string) => void; sending: boolean }) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')

  return (
    <Popover open={open} onOpenChange={(next) => { setOpen(next); if (!next) setReason('') }}>
      <PopoverTrigger asChild>
        <Button variant="destructive" size="sm" className="min-h-[44px]" disabled={sending}>
          {bt(lang, 'reject')}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="end">
        <div className="space-y-3">
          <label className="block text-xs font-medium text-muted-foreground">
            {bt(lang, 'rejectReasonLabel')}
          </label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={bt(lang, 'rejectReasonPlaceholder')}
            className="min-h-[40px]"
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" size="sm" className="min-h-[44px]" onClick={() => setOpen(false)}>
              {bt(lang, 'rejectCancel')}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="min-h-[44px]"
              disabled={!reason.trim()}
              onClick={() => {
                onReject(reason.trim())
                setOpen(false)
                setReason('')
              }}
            >
              {bt(lang, 'rejectSubmit')}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

interface Props {
  order: BoardOrderRow
  lang: Lang
  roles: string[]
  now: Date
  isNew: boolean
  pendingAdvance: OrderStatusValue | null
  onAdvance: (orderId: string, toStatus: OrderStatusValue) => void
  onReject: (orderId: string, notes: string) => void
  // R197 (b): all optional, so a caller that knows nothing of the one-click path (aging.test.ts)
  // renders as before. No fact, no button.
  fact?: FinalCheckFact
  finalCheckState?: FinalCheckRowState
  onFinalCheck?: (orderId: string, totalNow: string) => void
  // R264 (b): a write for this order is waiting for the server's reply. Optional (default false)
  // so a caller that knows nothing of it (aging.test.ts) renders as before.
  sending?: boolean
}

export function OrderCard({
  order, lang, roles, now, isNew, pendingAdvance, onAdvance, onReject, fact, finalCheckState, onFinalCheck,
  sending = false,
}: Props) {
  // The advance button last pressed in this mount, so the sending label sits on that one.
  const [pressed, setPressed] = useState<OrderStatusValue | null>(null)
  // R460 follow-up: the code link and Open share one guard, so a double tap makes one entry.
  const onOpen = useOpenOnce(`/orders/${order.id}`)
  const displayStatus = pendingAdvance ?? order.status
  const fcState: FinalCheckRowState = finalCheckState ?? { kind: 'idle' }
  const showFinalCheck =
    order.status === 'AWAITING_FINAL_CHECK' && canFinalCheck(roles) && !!fact && !!onFinalCheck
  const aging = computeAging(order, now)
  const fulfillmentKey = order.fulfillmentMethod === 'pickup' ? 'pickup' : order.fulfillmentMethod === 'delivery' ? 'delivery' : 'unknown'
  const prepTargets = getForwardAdvanceTargets(order.status)
  const cityName = lang === 'ar'
    ? (order.deliveryLocation?.cityNameAr ?? order.deliveryZone?.nameAr)
    : (order.deliveryLocation?.cityNameEn ?? order.deliveryZone?.nameEn)

  return (
    <Card
      className={cn(
        'relative overflow-hidden border-s-4',
        FULFILLMENT_BORDER_CLASS[fulfillmentKey],
        isNew && 'ring-2 ring-primary',
      )}
    >
      {isNew && (
        <span className="absolute end-2 top-2 z-10 inline-flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground">
          <Sparkles className="h-3 w-3" aria-hidden />
          {bt(lang, 'newBadge')}
        </span>
      )}

      <CardHeader className={cn('flex flex-row items-center justify-between gap-2 py-3', AGING_HEADER_CLASS[aging.band])}>
        <span className="flex items-center gap-2">
          {/* The order code and the footer's Open navigate; the card itself stays non-interactive so the
              footer's prep-advance/Reject buttons aren't nested inside a clickable wrapper. */}
          <Link to={`/orders/${order.id}`} onClick={onOpen} className="text-sm font-semibold text-foreground hover:underline">
            <Ltr>{formatOrderId(order.id, order.shortCode)}</Ltr>
          </Link>
          <Badge variant={BADGE_VARIANT[displayStatus]} className={pendingAdvance ? 'animate-pulse' : ''}>
            {STATUS_BADGE_LABEL[displayStatus][lang]}
          </Badge>
        </span>
        <AgingChip aging={aging} lang={lang} />
      </CardHeader>

      <CardContent className="space-y-2 pt-3 text-sm">
        <p className="truncate font-medium text-foreground">
          <bdi>{order.customer.name ?? order.customer.masterCustomerId}</bdi>
        </p>

        <p className="flex items-center justify-between text-muted-foreground">
          <span>{order._count.items} {order._count.items === 1 ? bt(lang, 'item') : bt(lang, 'items')}</span>
          <span>{order.fulfillmentMethod === 'pickup' ? bt(lang, 'pickup') : bt(lang, 'delivery')}</span>
        </p>

        {order.fulfillmentMethod !== 'pickup' && cityName && (
          <p className="truncate text-muted-foreground">
            <bdi>{cityName}</bdi>
          </p>
        )}

        <p className="flex items-center justify-between font-medium text-foreground">
          <Ltr>{formatMoney(order.totalAmount, order.currency)}</Ltr>
        </p>

        {order.dispatch && <DispatchBadge dispatch={order.dispatch} lang={lang} />}
      </CardContent>

      {/* ux2 (b): every card has a footer now, for the full-width Open below the actions. */}
      {
        <CardFooter className="flex flex-wrap items-center gap-2 pt-0" aria-busy={sending ? 'true' : undefined}>
          {showFinalCheck && (
            <FinalCheckButton
              orderId={order.id} fact={fact!} state={fcState} lang={lang} onFinalCheck={onFinalCheck!} sending={sending}
            />
          )}
          {showFinalCheck && fcState.kind === 'rejected' && (
            <p role="alert" className="w-full text-xs text-destructive">
              {fillSlots(bt(lang, 'finalCheckRejected'), { message: fcState.message })}
            </p>
          )}
          {prepTargets.map((target, i) => {
            const showSending = sending && (pressed ? pressed === target : i === 0)
            return (
              <Button
                key={target}
                size="sm"
                className="min-h-[44px]"
                disabled={!!pendingAdvance || sending}
                onClick={() => {
                  setPressed(target)
                  onAdvance(order.id, target)
                }}
              >
                {showSending ? (
                  <>
                    <Loader2 className="me-1.5 h-4 w-4 animate-spin" aria-hidden />
                    {bt(lang, 'sending')}
                  </>
                ) : (
                  STATUS_ADVANCE_LABEL[target][lang]
                )}
              </Button>
            )
          })}
          {canReject(roles) && (
            <RejectPopover lang={lang} sending={sending} onReject={(notes) => onReject(order.id, notes)} />
          )}
          {/* ux2 (b): one tap into the order, the same route as the code link above; its own
              full-width row, apart from advance and Reject. A link, so it is never disabled. */}
          <Button asChild variant="outline" className="min-h-[44px] w-full">
            <Link to={`/orders/${order.id}`} onClick={onOpen} data-testid="card-open">
              {bt(lang, 'open')}
              <span className="sr-only"> <Ltr>{formatOrderId(order.id, order.shortCode)}</Ltr></span>
            </Link>
          </Button>
        </CardFooter>
      }
    </Card>
  )
}
